import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { Mail, Send } from "lucide-react";
import { api, useApiMutation } from "../../lib/api";
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  CardList,
  CardRow,
  Empty,
  ErrorState,
  Fact,
  FactList,
  InlineCode,
  PageHeader,
  Segmented,
  Skeleton,
  TabBar,
  cx,
  notifyError,
  toaster,
} from "../../components/ui";
import { usePageTitle } from "./components";

// Mirrors apps/api/src/emails/routes.ts (the catalogue is apps/api/src/emails/catalogue.ts).

type SenderStatus = { state: "sending"; via: string; from: string } | { state: "logged"; set: string };

interface EmailMeta {
  id: string;
  group: string;
  name: string;
  when: string;
  who: string;
  why: string;
  senders: string[];
  fromName: string;
  unsubscribe: { can: boolean; how: string };
  layout: "framed" | "plain-html" | "text";
  source: string;
  gaps: string[];
  note: string | null;
  status: SenderStatus;
}

interface EmailDetail extends EmailMeta {
  rendered: { subject: string; text: string; html: string | null };
}

const KEY = ["admin", "emails"];

/** Sending or logged only, as an icon and a word. */
function SendingBadge({ status }: { status: SenderStatus }) {
  return status.state === "sending" ? (
    <Badge tone="good">
      <Send size={11} aria-hidden /> Sending
    </Badge>
  ) : (
    <Badge tone="warn">
      <Mail size={11} aria-hidden /> Logged only
    </Badge>
  );
}

const senderLine = (s: SenderStatus) => (s.state === "sending" ? `Sending from ${s.from} (${s.via}).` : `Logged, not sent. Set ${s.set} to send it.`);

/** Every email the platform sends, grouped, with a preview and a test send for each. */
export function AdminEmails() {
  usePageTitle("Emails");
  const [params, setParams] = useSearchParams();
  const id = params.get("email");
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ groups: string[]; emails: EmailMeta[] }>("/admin/emails") });

  const open = (next: string | null) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next) p.set("email", next);
        else p.delete("email");
        return p;
      },
      { preventScrollReset: true },
    );
  };

  // Closing a preview returns focus to its email in the list, even when the page opened on a linked preview.
  const shown = useRef(id);
  useEffect(() => {
    const closed = shown.current;
    shown.current = id;
    if (closed && !id) document.getElementById(`email-${closed}`)?.focus();
  }, [id]);

  const logged = q.data?.emails.filter((e) => e.status.state === "logged").length ?? 0;
  const groups = q.data ? q.data.groups.map((g) => ({ name: g, emails: q.data.emails.filter((e) => e.group === g) })).filter((g) => g.emails.length) : [];

  return (
    <>
      <PageHeader
        title="Emails"
        subtitle="Every email Metrickle sends: when it goes, who gets it and why. Previews use sample data from Quillmere, a fictional company, and tests go only to you."
      />
      <div className="max-w-5xl space-y-4">
        {logged > 0 && (
          <Callout tone="warn" title={`${logged} of ${q.data!.emails.length} emails are logged, not sent, here`}>
            Their sender variable isn't set in this environment, so they're written to the Worker logs instead. Each one says which variable to set.
          </Callout>
        )}
        {id && <EmailPreview id={id} onClose={() => open(null)} />}
        {q.isError ? (
          <Card>
            <ErrorState title="Couldn't load the emails" error={q.error} />
          </Card>
        ) : q.isPending ? (
          <Card aria-busy="true">
            <CardHeader title="Emails" />
            <CardList loading />
          </Card>
        ) : !groups.length ? (
          <Card>
            <Empty title="No emails in the catalogue" icon={<Mail size={24} aria-hidden />}>
              Emails are listed from apps/api/src/emails/catalogue.ts. Add an entry there for each email the platform sends.
            </Empty>
          </Card>
        ) : (
          groups.map((g) => (
            <Card key={g.name}>
              <CardHeader title={g.name} subtitle={`${g.emails.length} ${g.emails.length === 1 ? "email" : "emails"}`} />
              <CardList>
                {g.emails.map((e) => (
                  <CardRow key={e.id} wrap className={cx("items-start", id === e.id && "bg-surface-2")}>
                    <div className="min-w-0 flex-1 text-sm">
                      <h3 className="font-medium">
                        <Button id={`email-${e.id}`} variant="link" onClick={() => open(e.id)} aria-current={id === e.id || undefined}>
                          {e.name}
                        </Button>
                      </h3>
                      <dl className="mt-1 grid gap-x-3 gap-y-0.5 text-xs @2xl:grid-cols-[auto_1fr]">
                        <dt className="font-medium text-ink-2">When</dt>
                        <dd className="text-ink-3">{e.when}</dd>
                        <dt className="font-medium text-ink-2">Who</dt>
                        <dd className="text-ink-3">{e.who}</dd>
                        <dt className="font-medium text-ink-2">Why</dt>
                        <dd className="text-ink-3">{e.why}</dd>
                      </dl>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {e.gaps.length > 0 && <Badge tone="warn">Needs work</Badge>}
                      <SendingBadge status={e.status} />
                    </div>
                  </CardRow>
                ))}
              </CardList>
            </Card>
          ))
        )}
      </div>
    </>
  );
}

const WIDTHS = { desktop: 600, phone: 375 } as const;

function EmailPreview({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: [...KEY, id], queryFn: () => api<EmailDetail>(`/admin/emails/${encodeURIComponent(id)}`) });
  const heading = useRef<HTMLHeadingElement>(null);
  const [tab, setTab] = useState<"html" | "text">("html");
  const [width, setWidth] = useState<keyof typeof WIDTHS>("desktop");
  const test = useApiMutation(
    () => api<{ result: "sent" | "logged"; to: string; status: SenderStatus }>(`/admin/emails/${encodeURIComponent(id)}/test`, { method: "POST" }),
    [],
  );

  // Opening another email moves focus to its heading, so the change is heard.
  useEffect(() => {
    if (q.data) heading.current?.focus();
  }, [q.data?.id]);
  useEffect(() => setTab("html"), [id]);

  if (q.isError) {
    return (
      <Card>
        <ErrorState title="Couldn't load this email" error={q.error} />
        <div className="flex justify-end px-4 pb-4">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Back to all emails
          </Button>
        </div>
      </Card>
    );
  }
  if (!q.data) return <Skeleton className="h-96" />;
  const e = q.data;
  const r = e.rendered;
  const hasHtml = r.html !== null;
  const showing = hasHtml ? tab : "text";

  const sendTest = () =>
    test.mutate(undefined, {
      onSuccess: (res) =>
        res.result === "sent"
          ? toaster.success({ title: `Test sent to ${res.to}` })
          : toaster.info({ title: "Test logged, not sent", description: res.status.state === "logged" ? `Set ${res.status.set} to send it.` : undefined }),
      onError: notifyError,
    });

  return (
    <Card aria-labelledby="email-title">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4">
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <SendingBadge status={e.status} />
            <span className="text-xs text-ink-3">{e.group}</span>
          </div>
          <h2 id="email-title" ref={heading} tabIndex={-1} className="text-lg font-semibold text-ink outline-none">
            {e.name}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="primary" pending={test.isPending} pendingLabel="Sending…" onClick={sendTest}>
            <Send size={14} aria-hidden /> Send me a test
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Back to all emails
          </Button>
        </div>
      </div>

      <FactList cols={2} className="px-4 py-4">
        <Fact term="When it sends">{e.when}</Fact>
        <Fact term="Who gets it">{e.who}</Fact>
        <Fact term="Why they get it">{e.why}</Fact>
        <Fact term="Can they stop it?">{e.unsubscribe.how}</Fact>
        <Fact term="Sender">
          {senderLine(e.status)} From name “{e.fromName}”.
          {e.senders.length > 1 && <> Uses the first one set of {e.senders.map((s, i) => <span key={s}>{i > 0 && ", then "}<InlineCode>{s}</InlineCode></span>)}.</>}
        </Fact>
        <Fact term="Built in">
          <InlineCode className="break-all">{e.source}</InlineCode>
        </Fact>
      </FactList>

      {(e.gaps.length > 0 || e.note) && (
        <div className="space-y-2 px-4 pb-4">
          {e.gaps.length > 0 && (
            <Callout tone="warn" title="Needs work">
              <ul className="list-disc pl-4">
                {e.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </Callout>
          )}
          {e.note && <Callout>{e.note}</Callout>}
        </div>
      )}

      <div className="border-t border-line px-4 py-3">
        <p className="text-xs text-ink-3">Subject</p>
        <p className="text-sm font-medium break-words text-ink">{r.subject}</p>
        <p className="mt-1 text-xs text-ink-3">Test sends add “[Test]” in front.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
        {hasHtml ? (
          <TabBar
            aria-label="Email version"
            panelId="email-panel"
            items={[
              { value: "html", label: "HTML" },
              { value: "text", label: "Plain text" },
            ]}
            value={tab}
            onChange={(v) => setTab(v as "html" | "text")}
          />
        ) : (
          <p className="text-xs text-ink-2">Sent as plain text only.</p>
        )}
        {showing === "html" && (
          <Segmented
            aria-label="Preview width"
            items={[
              { value: "desktop", label: `Desktop (${WIDTHS.desktop}px)` },
              { value: "phone", label: `Phone (${WIDTHS.phone}px)` },
            ]}
            value={width}
            onChange={(v) => setWidth(v as keyof typeof WIDTHS)}
          />
        )}
      </div>

      <div id="email-panel" role={hasHtml ? "tabpanel" : undefined} aria-label={showing === "html" ? "HTML" : "Plain text"} className="border-t border-line bg-surface-2 p-4">
        {showing === "html" ? (
          <div className="overflow-x-auto">
            <iframe
              sandbox=""
              srcDoc={r.html!}
              title={`Preview of “${r.subject}”`}
              width={WIDTHS[width]}
              height={640}
              className="mx-auto block max-w-none rounded-lg border border-line"
            />
          </div>
        ) : (
          <pre tabIndex={0} role="region" aria-label="Plain text version" className="max-h-[640px] overflow-auto rounded-lg border border-line bg-surface p-4 font-mono text-[13px] break-words whitespace-pre-wrap text-ink">
            {r.text}
          </pre>
        )}
      </div>
    </Card>
  );
}
